import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  VStack,
  Heading,
  Text,
  Input,
  Button,
  HStack,
  Link,
} from "@chakra-ui/react";
import { useFormik } from "formik";
import * as Yup from "yup";
import AppCard from "components/reusables/AppCard/AppCard";
import { loginRequest, registerRequest } from "store/features/user/userActions";
import { RootState } from "store/rootReducer";
import { clearRegisterState } from "store/features/user/userSlice";

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const Register: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");

  const location = useLocation();

  const from = (location.state as any)?.from || "/";

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const {
    registerLoading,
    registerError,
    registerSuccessMessage,
    registerSuccess,
  } = useSelector((state: RootState) => state.user);

  // success ke baad login page pe bhej do
  useEffect(() => {
    if (registerSuccess) {

      dispatch(loginRequest({ email, password }));
      navigate(from, { replace: true });
      dispatch(clearRegisterState());
    }
  }, [registerSuccessMessage, navigate]);

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
    password: Yup.string()
      .min(6, "Password must be at least 6 characters")
      .required("Password is required"),
    fullName: Yup.string().required("Full name is required"),
    mobile: Yup.string().required("Mobile is required"),
    aadharNumber: Yup.string(),
    panNumber: Yup.string(),
    address: Yup.string(),
  });

  const formik = useFormik({
    initialValues: {
      email: "",
      mobile: "",
      password: "",
      fullName: "",
      aadharNumber: "",
      panNumber: "",
      address: "",
      //  default empty string -> backend ko "" hi milega jab user image na de
      profileImage: "",
      imgExtenstion: "",
    },
    validationSchema,
    onSubmit: (values) => {
      // safety: agar image nahi hai to ensure "" hi rahe
      const payload = {
        ...values,
        profileImage: values.profileImage || "",
        imgExtenstion: values.imgExtenstion || ".jpg",
      };

      setEmail(payload.email);
      setPassword(payload.password);
      // console.log({ payload });
      dispatch(registerRequest(payload));
    },
  });

  // Remove image handler
  const handleRemoveImage = () => {
    formik.setFieldValue("profileImage", "");
    formik.setFieldValue("imgExtenstion", "");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <AppCard
      header={"Register"}
      headerClass="text-2xl font-bold text-gray-700 align-left"
    >
      <Box>
        <VStack gap={6} w="full">
          <Heading
            size="xl"
            textAlign="center"
            color="brand.700"
            fontWeight="bold"
          >
            Create Your Account
          </Heading>

          <Box w="full">
            <form onSubmit={formik.handleSubmit}>
              <VStack gap={5} w="full">
                {/* Full Name */}
                <Box w="full">
                  <Text fontWeight="semibold" color="gray.700" mb={1}>
                    Full Name
                  </Text>
                  <Input
                    name="fullName"
                    value={formik.values.fullName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter your full name"
                    size="lg"
                    borderRadius="lg"
                    borderColor="gray.300"
                    _focus={{
                      borderColor: "brand.500",
                      boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)",
                    }}
                  />
                  {formik.touched.fullName && formik.errors.fullName && (
                    <Text fontSize="sm" color="fail.700" mt={1}>
                      {formik.errors.fullName}
                    </Text>
                  )}
                </Box>

                {/* Email */}
                <Box w="full">
                  <Text fontWeight="semibold" color="gray.700" mb={1}>
                    Email Address
                  </Text>
                  <Input
                    type="email"
                    name="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter your email"
                    size="lg"
                    borderRadius="lg"
                    borderColor="gray.300"
                    _focus={{
                      borderColor: "brand.500",
                      boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)",
                    }}
                  />
                  {formik.touched.email && formik.errors.email && (
                    <Text fontSize="sm" color="fail.700" mt={1}>
                      {formik.errors.email}
                    </Text>
                  )}
                </Box>

                {/* Mobile */}
                <Box w="full">
                  <Text fontWeight="semibold" color="gray.700" mb={1}>
                    Mobile
                  </Text>
                  <Input
                    name="mobile"
                    value={formik.values.mobile}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter your mobile number"
                    size="lg"
                    borderRadius="lg"
                    borderColor="gray.300"
                    _focus={{
                      borderColor: "brand.500",
                      boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)",
                    }}
                  />
                  {formik.touched.mobile && formik.errors.mobile && (
                    <Text fontSize="sm" color="fail.700" mt={1}>
                      {formik.errors.mobile}
                    </Text>
                  )}
                </Box>

                {/* Password */}
                <Box w="full">
                  <Text fontWeight="semibold" color="gray.700" mb={1}>
                    Password
                  </Text>
                  <Input
                    type="password"
                    name="password"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter your password"
                    size="lg"
                    borderRadius="lg"
                    borderColor="gray.300"
                    _focus={{
                      borderColor: "brand.500",
                      boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)",
                    }}
                    autoComplete="off"
                  />
                  {formik.touched.password && formik.errors.password && (
                    <Text fontSize="sm" color="fail.700" mt={1}>
                      {formik.errors.password}
                    </Text>
                  )}
                </Box>

                {/* Aadhaar */}
                <Box w="full">
                  <Text fontWeight="semibold" color="gray.700" mb={1}>
                    Aadhar Number
                  </Text>
                  <Input
                    name="aadharNumber"
                    value={formik.values.aadharNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter Aadhaar number"
                    size="lg"
                    borderRadius="lg"
                    borderColor="gray.300"
                    _focus={{
                      borderColor: "brand.500",
                      boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)",
                    }}
                  />
                </Box>

                {/* PAN */}
                <Box w="full">
                  <Text fontWeight="semibold" color="gray.700" mb={1}>
                    PAN Number
                  </Text>
                  <Input
                    name="panNumber"
                    value={formik.values.panNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter PAN number"
                    size="lg"
                    borderRadius="lg"
                    borderColor="gray.300"
                    _focus={{
                      borderColor: "brand.500",
                      boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)",
                    }}
                  />
                </Box>

                {/* Address */}
                <Box w="full">
                  <Text fontWeight="semibold" color="gray.700" mb={1}>
                    Address
                  </Text>
                  <Input
                    name="address"
                    value={formik.values.address}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter address"
                    size="lg"
                    borderRadius="lg"
                    borderColor="gray.300"
                    _focus={{
                      borderColor: "brand.500",
                      boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)",
                    }}
                  />
                </Box>

                {/* Profile Image */}
                <Box w="full">
                  <Text fontWeight="semibold" color="gray.700" mb={1}>
                    Profile Image
                  </Text>
                  <Input
                    type="file"
                    accept="image/*"
                    ref={fileInputRef}
                    onChange={async (e) => {
                      const file = e.currentTarget.files?.[0];
                      if (!file) {
                        // koi file nahi, ensure empty string
                        formik.setFieldValue("profileImage", "");
                        formik.setFieldValue("imgExtenstion", "");
                        return;
                      }

                      const base64 = await fileToBase64(file); // "data:image/png;base64,xxx"
                      const parts = base64.split(",");
                      const pureBase64 = parts.length > 1 ? parts[1] : parts[0];

                      const ext =
                        file.name.split(".").pop()?.toLowerCase() || "";

                      formik.setFieldValue("profileImage", pureBase64);
                      formik.setFieldValue("imgExtenstion", ext);
                    }}
                    size="lg"
                    borderRadius="lg"
                    borderColor="gray.300"
                    _focus={{
                      borderColor: "brand.500",
                      boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)",
                    }}
                  />

                  {(formik.values.imgExtenstion ||
                    formik.values.profileImage) && (
                    <HStack mt={2} gap={3}>
                      {formik.values.imgExtenstion && (
                        <Text fontSize="sm" color="gray.500">
                          Selected: .{formik.values.imgExtenstion}
                        </Text>
                      )}
                      <Button
                        size="xs"
                        variant="outline"
                        colorScheme="red"
                        onClick={handleRemoveImage}
                      >
                        <Text color={"red.500"}>X</Text>
                      </Button>
                    </HStack>
                  )}
                </Box>

                {registerError && (
                  <Box
                    p={4}
                    bg="fail.50"
                    color="fail.700"
                    borderRadius="lg"
                    w="full"
                    border="1px solid"
                    borderColor="fail.200"
                  >
                    {registerError}
                  </Box>
                )}

                {registerSuccessMessage && (
                  <Box
                    p={4}
                    bg="success.50"
                    color="success.700"
                    borderRadius="lg"
                    w="full"
                    border="1px solid"
                    borderColor="success.200"
                  >
                    {registerSuccessMessage}
                  </Box>
                )}

                {/* Submit */}
                <Button
                  type="submit"
                  bg="brand.500"
                  color="white"
                  _hover={{ bg: "brand.600" }}
                  _active={{ bg: "brand.700" }}
                  w="full"
                  size="lg"
                  borderRadius="lg"
                  loading={registerLoading} // agar custom Button prop hai
                  fontWeight="semibold"
                >
                  Register
                </Button>
              </VStack>
            </form>
          </Box>
        </VStack>
        <Box borderColor="gray.200" className="display-flex text-center my-4">
          <Link href="/" display="flex" alignItems="center" gap={2}>
            <Text
              fontSize="sm"
              fontWeight="medium"
              color={"brand.600"}
              textDecoration="underline"
            >
              back to home
            </Text>
          </Link>
        </Box>
      </Box>
    </AppCard>
  );
};

export default Register;

// import React, { useState } from "react";
// import {
//   Box,
//   Button,
//   VStack,
//   Input,
//   Text,
//   Heading,
//   HStack,
//   Field,
// } from "@chakra-ui/react";
// // import { FormSkeleton } from '../ui/SkeletonLoader';

// const Register: React.FC = () => {
//   const [step, setStep] = useState<"details" | "otp">("details");
//   const [firstName, setFirstName] = useState("");
//   const [lastName, setLastName] = useState("");
//   const [email, setEmail] = useState("");
//   const [password, setPassword] = useState("");
//   const [otp, setOtp] = useState("");
//   const [isLoading, setIsLoading] = useState(false);
//   const [error, setError] = useState("");
//   const [success, setSuccess] = useState("");

//   const handleDetailsSubmit = async (e: React.FormEvent) => {
//     e.preventDefault();
//     setError("");
//     setSuccess("");
//     setIsLoading(true);

//   };

//   const handleBackToDetails = () => {
//     setStep("details");
//     setOtp("");
//     setError("");
//     setSuccess("");
//   };

//   return (
//     <Box
//     >
//       <VStack gap={6}>
//         <Heading
//           size="xl"
//           textAlign="center"
//           color="brand.700"
//           fontWeight="bold"
//         >
//           {step === "details" ? "Create Your Account" : "Verify Your Identity"}
//         </Heading>

//         {error && (
//           <Box
//             p={4}
//             bg="fail.50"
//             color="fail.700"
//             borderRadius="lg"
//             w="full"
//             border="1px solid"
//             borderColor="fail.200"
//           >
//             {error}
//           </Box>
//         )}

//         {success && (
//           <Box
//             p={4}
//             bg="success.50"
//             color="success.700"
//             borderRadius="lg"
//             w="full"
//             border="1px solid"
//             borderColor="success.200"
//           >
//             {success}
//           </Box>
//         )}

//         {step === "details" ? (
//           <Box as="form" onSubmit={handleDetailsSubmit} w="full">
//             <VStack gap={5}>
//               <HStack gap={3} w="full">
//                 <Field.Root required flex="1">
//                   <Field.Label fontWeight="semibold" color="gray.700">
//                     First Name
//                   </Field.Label>
//                   <Input
//                     value={firstName}
//                     onChange={(e) => setFirstName(e.target.value)}
//                     placeholder="First name"
//                     size="lg"
//                     borderRadius="lg"
//                     borderColor="gray.300"
//                     _focus={{
//                       borderColor: "brand.500",
//                       boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)",
//                     }}
//                   />
//                 </Field.Root>

//                 <Field.Root required flex="1">
//                   <Field.Label fontWeight="semibold" color="gray.700">
//                     Last Name
//                   </Field.Label>
//                   <Input
//                     value={lastName}
//                     onChange={(e) => setLastName(e.target.value)}
//                     placeholder="Last name"
//                     size="lg"
//                     borderRadius="lg"
//                     borderColor="gray.300"
//                     _focus={{
//                       borderColor: "brand.500",
//                       boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)",
//                     }}
//                   />
//                 </Field.Root>
//               </HStack>

//               <Field.Root required>
//                 <Field.Label fontWeight="semibold" color="gray.700">
//                   Email Address
//                 </Field.Label>
//                 <Input
//                   type="email"
//                   value={email}
//                   onChange={(e) => setEmail(e.target.value)}
//                   placeholder="Enter your email"
//                   size="lg"
//                   borderRadius="lg"
//                   borderColor="gray.300"
//                   _focus={{
//                     borderColor: "brand.500",
//                     boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)",
//                   }}
//                 />
//               </Field.Root>

//               <Field.Root required>
//                 <Field.Label fontWeight="semibold" color="gray.700">
//                   Password
//                 </Field.Label>
//                 <Input
//                   type="password"
//                   value={password}
//                   onChange={(e) => setPassword(e.target.value)}
//                   placeholder="Enter your password"
//                   size="lg"
//                   borderRadius="lg"
//                   borderColor="gray.300"
//                   _focus={{
//                     borderColor: "brand.500",
//                     boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)",
//                   }}
//                 />
//               </Field.Root>

//               <Button
//                 type="submit"
//                 bg="brand.500"
//                 color="white"
//                 _hover={{ bg: "brand.600" }}
//                 _active={{ bg: "brand.700" }}
//                 w="full"
//                 size="lg"
//                 borderRadius="lg"
//                 loading={isLoading}
//                 fontWeight="semibold"
//               >
//                 Send OTP
//               </Button>
//             </VStack>
//           </Box>
//         ) : (
//           <VStack gap={5} w="full">
//             <Text textAlign="center" color="gray.600" fontSize="md">
//               Enter the 4-digit OTP sent to your email
//             </Text>

//             <HStack justify="center" gap={3}>
//               {[0, 1, 2, 3].map((index) => (
//                 <Input
//                   key={index}
//                   type="text"
//                   maxLength={1}
//                   value={otp[index] || ""}
//                   onChange={(e) => {
//                     const value = e.target.value;
//                     if (value.length <= 1) {
//                       const newOtp = otp.split("");
//                       newOtp[index] = value;
//                       setOtp(newOtp.join(""));
//                     }
//                   }}
//                   w="60px"
//                   h="60px"
//                   textAlign="center"
//                   fontSize="xl"
//                   fontWeight="bold"
//                   borderRadius="lg"
//                   borderWidth={2}
//                   borderColor="gray.300"
//                   _focus={{
//                     borderColor: "brand.500",
//                     boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)",
//                   }}
//                   _file={{
//                     bg: "brand.50",
//                     borderColor: "brand.300",
//                   }}
//                 />
//               ))}
//             </HStack>
//           </VStack>
//         )}
//       </VStack>
//     </Box>
//   );
// };

// export default Register;
